import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FiArrowLeft, FiCheck, FiX, FiAward } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const StudentQuizResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: result, isLoading } = useQuery({
    queryKey: ['student-quiz-result', id],
    queryFn: () => axios.get(`/api/quizzes/${id}/result`).then(res => res.data.result)
  });

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!result) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Result not found</p>
        <Link to="/quizzes" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          Back to Quizzes
        </Link>
      </div>
    );
  }

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900">Your Quiz Result</h1>
      </div>

      <div className="card text-center mb-6">
        <FiAward className="mx-auto text-primary-600 mb-4" size={64} />
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <p className="text-5xl font-bold mb-2">
            <span className={getScoreColor(result.percentage)}>
              {result.score}
            </span>
            <span className="text-gray-400 text-3xl">/{result.totalPoints}</span>
          </p>
          <p className="text-lg text-gray-600">
            {result.percentage.toFixed(1)}% Correct
          </p>
        </div>

        <p className="text-sm text-gray-500">
          Completed on {new Date(result.completedAt).toLocaleDateString()}
        </p>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Review Your Answers
        </h2>

        <div className="space-y-6">
          {result.questions?.map((question, index) => (
            <div
              key={question._id}
              className={`p-4 rounded-lg border-2 ${
                question.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  {index + 1}. {question.question}
                </h3>
                <div className="flex items-center">
                  {question.isCorrect ? (
                    <FiCheck className="text-green-600 mr-1" />
                  ) : (
                    <FiX className="text-red-600 mr-1" />
                  )}
                  <span className="text-sm font-medium">
                    {question.isCorrect ? question.points : 0}/{question.points} pts
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {question.options?.map((option, optionIndex) => (
                  <div
                    key={optionIndex}
                    className={`p-2 rounded flex items-start ${
                      optionIndex === question.correctAnswer
                        ? 'bg-green-100 font-medium text-green-800'
                        : optionIndex === question.userAnswer && !question.isCorrect
                        ? 'bg-red-100 text-red-800'
                        : ''
                    }`}
                  >
                    <span className="mr-2 flex-shrink-0">
                      {optionIndex === question.correctAnswer && '✓'}
                      {optionIndex === question.userAnswer && !question.isCorrect && '✗'}
                      {optionIndex === question.userAnswer && question.isCorrect && '✓'}
                    </span>
                    <span>{option}</span>
                  </div>
                ))}
              </div>

              {!question.isCorrect && question.userAnswer !== null && question.userAnswer !== undefined && (
                <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Your answer:</span> Option {(question.userAnswer || 0) + 1}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Correct answer:</span> Option {question.correctAnswer + 1}
                  </p>
                </div>
              )}
              
              {(question.userAnswer === null || question.userAnswer === undefined) && (
                <div className="mt-3 p-2 bg-yellow-100 rounded text-sm">
                  <p className="text-yellow-800">
                    Not answered • Correct answer: Option {question.correctAnswer + 1}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentQuizResult;